import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Power, Plus, Upload, Trash2, FileText, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30";

const EMPTY = { name: "", alias: "", role: "", kill_count: "", weapon: "", first_appearance: "", bio: "", sort_order: 0 };

export default function AdminDossiers({ cfg }) {
  const [enabled, setEnabled] = useState(null);
  const [characters, setCharacters] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingImage, setEditingImage] = useState(null);

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditingImage(c.image);
    setImageFile(null);
    setImagePreview(null);
    setForm({
      name: c.name,
      alias: c.alias || "",
      role: c.role || "",
      kill_count: c.kill_count != null ? String(c.kill_count) : "",
      weapon: c.weapon || "",
      first_appearance: c.first_appearance || "",
      bio: c.bio,
      sort_order: c.sort_order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingImage(null);
    setForm(EMPTY);
    setImageFile(null);
    setImagePreview(null);
  };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/characters`, cfg());
      setEnabled(r.data.enabled);
      setCharacters(r.data.characters);
    } catch {
      toast.error("Couldn't open the case files.");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async () => {
    try {
      const r = await axios.post(`${API}/admin/dossiers-toggle`, { open: !enabled }, cfg());
      setEnabled(r.data.enabled);
      toast.success(r.data.enabled ? "Dossiers are LIVE on the homepage." : "Dossiers hidden. The files stay sealed.");
    } catch {
      toast.error("The switch stuck.");
    }
  };

  const addCharacter = async (e) => {
    e.preventDefault();
    if (!editingId && !imageFile) {
      toast.error("A dossier needs artwork.");
      return;
    }
    setAdding(true);
    try {
      let imageUrl = editingImage;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const up = await axios.post(`${API}/admin/upload-cover`, fd, cfg());
        imageUrl = up.data.url;
      }
      const payload = {
        ...form,
        kill_count: form.kill_count === "" ? undefined : parseInt(form.kill_count, 10),
        alias: form.alias || undefined,
        role: form.role || undefined,
        weapon: form.weapon || undefined,
        first_appearance: form.first_appearance || undefined,
        image: imageUrl,
      };
      if (editingId) {
        await axios.patch(`${API}/admin/characters/${editingId}`, payload, cfg());
        toast.success(`${form.name}'s file amended.`);
      } else {
        await axios.post(`${API}/admin/characters`, payload, cfg());
        toast.success(`${form.name} filed.`);
      }
      cancelEdit();
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't file that character.");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (c) => {
    try {
      await axios.delete(`${API}/admin/characters/${c.id}`, cfg());
      toast.success(`${c.name}'s file burned.`);
      load();
    } catch {
      toast.error("Couldn't burn that file.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div data-testid="admin-dossiers-panel" className="space-y-8">
      <div className="border border-blooddeep/50 bg-pulp p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-impact uppercase tracking-wider text-sm text-paper flex items-center gap-2">
              <Power size={15} className="text-bloodglow" /> Dossiers Section Switch
            </h3>
            <p className="font-type text-xs text-paper/50 mt-1">
              OFF keeps the homepage clean while you gather artwork and bios. Nothing renders until you flip it ON.
            </p>
          </div>
          <button
            data-testid="dossiers-toggle-button"
            onClick={toggle}
            disabled={enabled === null}
            className={`font-impact uppercase tracking-wider text-sm border-2 border-black px-6 py-3 ink-shadow-sm transition-colors duration-150 disabled:opacity-50 ${
              enabled ? "bg-crimson text-white hover:bg-blooddeep" : "bg-yell text-black hover:bg-yell/80"
            }`}
          >
            {enabled === null ? "..." : enabled ? "ON — Click to Hide" : "OFF — Click to Publish"}
          </button>
        </div>
      </div>

      <form data-testid="dossier-add-form" onSubmit={addCharacter} className="border border-blooddeep/50 bg-pulp p-6">
        <h3 className="font-impact uppercase tracking-wider text-sm text-paper mb-4 flex items-center gap-2">
          <Plus size={15} className="text-bloodglow" /> {editingId ? "Amend the File" : "File a New Character"}
        </h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <input data-testid="dossier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name (e.g. Mary Machinegun)" className={inputCls} required />
          <input data-testid="dossier-alias" value={form.alias} onChange={(e) => setForm({ ...form, alias: e.target.value })} placeholder="Alias (e.g. The Valkyrie)" className={inputCls} />
          <input data-testid="dossier-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role (e.g. Protagonist, Monster)" className={inputCls} />
          <input data-testid="dossier-kills" type="number" min="0" value={form.kill_count} onChange={(e) => setForm({ ...form, kill_count: e.target.value })} placeholder="Kill count" className={inputCls} />
          <input data-testid="dossier-weapon" value={form.weapon} onChange={(e) => setForm({ ...form, weapon: e.target.value })} placeholder="Weapon of choice" className={inputCls} />
          <input data-testid="dossier-first-appearance" value={form.first_appearance} onChange={(e) => setForm({ ...form, first_appearance: e.target.value })} placeholder="First appearance (e.g. Mary Machinegun #1)" className={inputCls} />
        </div>
        <textarea
          data-testid="dossier-bio"
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          placeholder="Bio — who they are, what they want, what they've done (max 1200 chars)"
          rows={4}
          maxLength={1200}
          className={`${inputCls} w-full resize-none mb-3`}
          required
        />
        <div className="flex flex-wrap items-center gap-3">
          <label
            data-testid="dossier-image-label"
            className="flex items-center gap-3 border-2 border-dashed border-crimson/50 bg-void px-4 py-3 cursor-pointer transition-colors duration-150 hover:border-crimson"
          >
            {imagePreview ? (
              <img src={imagePreview} alt="Dossier artwork preview" className="w-12 h-16 object-cover" />
            ) : editingId && editingImage ? (
              <img src={editingImage} alt="Current artwork" className="w-12 h-16 object-cover" />
            ) : (
              <Upload size={18} className="text-bloodglow" />
            )}
            <span className="font-courier text-xs text-paper/60">
              {imageFile ? imageFile.name : editingId ? "Swap artwork (optional)" : "Artwork (JPG/PNG/WebP — 5 MB max)"}
            </span>
            <input
              data-testid="dossier-image-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setImageFile(f);
                setImagePreview(f ? URL.createObjectURL(f) : null);
              }}
            />
          </label>
          <button
            data-testid="dossier-submit"
            disabled={adding}
            className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs bg-blooddeep text-white border-2 border-black px-5 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson disabled:opacity-50"
          >
            <FileText size={14} /> {adding ? "Filing..." : editingId ? "Save Changes" : "File Character"}
          </button>
          {editingId && (
            <button
              type="button"
              data-testid="dossier-cancel-edit"
              onClick={cancelEdit}
              className="font-impact uppercase tracking-wider text-xs text-paper/60 border border-paper/30 px-5 py-2.5 transition-colors duration-150 hover:text-paper"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3">
        {(characters || []).map((c) => (
          <div key={c.id} data-testid={`dossier-row-${c.id}`} className="border border-blooddeep/40 bg-pulp px-4 py-3 flex flex-wrap items-center gap-4">
            <img src={c.image} alt={`${c.name} thumbnail`} className="w-12 h-16 object-cover border border-blooddeep/40" />
            <div className="flex-1 min-w-[180px]">
              <p className="font-impact uppercase tracking-wide text-sm text-paper">
                {c.name}
                {c.alias && <span className="ml-2 font-marker text-[11px] text-bloodglow normal-case">a.k.a. {c.alias}</span>}
              </p>
              <p className="font-courier text-[11px] text-paper/50">
                {[
                  c.role,
                  c.kill_count != null ? `${c.kill_count} kills` : null,
                  c.weapon,
                ].filter(Boolean).join(" — ") || "details pending"}
              </p>
            </div>
            <button
              data-testid={`dossier-edit-${c.id}`}
              onClick={() => startEdit(c)}
              className="flex items-center gap-1.5 font-impact uppercase tracking-wider text-[11px] text-paper/60 border border-paper/30 px-3 py-2 transition-colors duration-150 hover:border-cyanx hover:text-cyanx"
            >
              <Pencil size={12} /> Edit
            </button>
            <button
              data-testid={`dossier-burn-${c.id}`}
              onClick={() => {
                if (deletingId === c.id) {
                  remove(c);
                } else {
                  setDeletingId(c.id);
                  setTimeout(() => setDeletingId((cur) => (cur === c.id ? null : cur)), 4000);
                }
              }}
              className={`flex items-center gap-1.5 font-impact uppercase tracking-wider text-[11px] border px-3 py-2 transition-colors duration-150 ${
                deletingId === c.id
                  ? "bg-blooddeep text-white border-black"
                  : "text-bloodglow border-blooddeep/50 hover:border-crimson hover:text-crimson"
              }`}
            >
              <Trash2 size={12} /> {deletingId === c.id ? "Sure?" : "Burn File"}
            </button>
          </div>
        ))}
        {characters && characters.length === 0 && (
          <p className="font-type text-sm text-paper/50">No characters filed yet. Add the first when you have art and bio in hand.</p>
        )}
      </div>
    </div>
  );
}
